import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/Header'
import { useAuth } from '../hooks/useAuth'
import { getItAssets, getItAssetsAccess, type ItAssetItem } from '../lib/apiPhpAdapter'
import {
  buildingOptionsFromItems,
  itemBuildingMatchesFilter,
  parseItAssetLocation,
  roomOptionsFromItems,
  userOptionsFromItems,
} from '../lib/itAssetLocation'
import { COMPUTER_LABS, itemMatchesAnyComputerLab, itemMatchesComputerLab } from '../lib/itComputerLabs'
import './ITAdminRestrictedTable.css'

const CATEGORY_OPTIONS: { id: string; label: string; cats: string[] }[] = [
  { id: 'notebook', label: 'Notebooky', cats: ['notebook'] },
  { id: 'pc', label: 'PC', cats: ['pc', 'pc_set'] },
  { id: 'monitor', label: 'Monitory', cats: ['monitor'] },
  { id: 'printer', label: 'Tiskárny', cats: ['printer'] },
  { id: 'projector', label: 'Projektory', cats: ['projector'] },
  { id: 'switch', label: 'Switche', cats: ['switch'] },
  { id: 'router', label: 'Routery', cats: ['router'] },
  { id: 'scanner', label: 'Scannery', cats: ['scanner'] },
  { id: 'interactive_board', label: 'Int. tabule', cats: ['interactive_board'] },
  { id: 'ups', label: 'UPS', cats: ['ups'] },
  { id: 'accessory', label: 'Příslušenství', cats: ['accessory'] },
]

const CATEGORY_LABELS: Record<string, string> = {
  notebook: 'Notebook',
  pc: 'PC',
  pc_set: 'Soubor PC',
  monitor: 'Monitor',
  printer: 'Tiskárna',
  projector: 'Projektor',
  switch: 'Switch',
  router: 'Router',
  scanner: 'Scanner',
  interactive_board: 'Int. tabule',
  ups: 'UPS',
  accessory: 'Příslušenství',
}

/** Sémantické pásma stáří (barva řádku). */
type AgeBand = 'new' | 'normal' | 'attention' | 'replace' | 'unknown'

type AgeProfile = 'display' | 'default'

const DISPLAY_CATEGORIES = new Set(['monitor', 'projector', 'interactive_board'])

const AGE_OPTIONS: { id: AgeBand; label: string }[] = [
  { id: 'new', label: 'Nedávno pořízené' },
  { id: 'normal', label: 'Normální životnost' },
  { id: 'attention', label: 'Pozornost / profylaktika' },
  { id: 'replace', label: 'Prioritní výměna' },
  { id: 'unknown', label: 'Bez roku' },
]

const AGE_BAND_CLASS: Record<Exclude<AgeBand, 'unknown'>, string> = {
  new: 'it-admin-restricted__row--age-new',
  normal: 'it-admin-restricted__row--age-normal',
  attention: 'it-admin-restricted__row--age-attention',
  replace: 'it-admin-restricted__row--age-replace',
}

type SortKey =
  | 'category'
  | 'inventory_number'
  | 'name'
  | 'building'
  | 'room'
  | 'user'
  | 'monitor_location'
  | 'year_acquired'
  | 'accounting_price'
  | 'su_au'
  | 'note'

type SortDir = 'asc' | 'desc'

const REF_YEAR = new Date().getFullYear()

function categoryLabel(cat: string): string {
  return CATEGORY_LABELS[cat] || cat
}

function formatPrice(v?: number | null): string {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency: 'CZK',
    maximumFractionDigits: 0,
  }).format(v)
}

function assetAge(yearAcquired?: number | null): number | null {
  if (yearAcquired == null || !Number.isFinite(yearAcquired) || yearAcquired < 1900) return null
  return REF_YEAR - yearAcquired
}

function ageProfileForCategory(category: string): AgeProfile {
  return DISPLAY_CATEGORIES.has(category) ? 'display' : 'default'
}

function ageBandOf(yearAcquired?: number | null, category?: string): AgeBand {
  const age = assetAge(yearAcquired)
  if (age === null) return 'unknown'
  if (age < 0) return 'new'

  if (ageProfileForCategory(category || '') === 'display') {
    // Monitory, projektory, interaktivní tabule: 0–5 / 5–8 / 8–10 / 10+
    if (age < 5) return 'new'
    if (age < 8) return 'normal'
    if (age < 10) return 'attention'
    return 'replace'
  }

  // PC, notebooky a ostatní: 0–3 / 3–5 / 5–7 / 7+
  if (age < 3) return 'new'
  if (age < 5) return 'normal'
  if (age < 7) return 'attention'
  return 'replace'
}

function ageBandClass(item: ItAssetItem): string | undefined {
  const band = ageBandOf(item.year_acquired, item.category)
  if (band === 'unknown') return undefined
  return AGE_BAND_CLASS[band]
}

function rowClassName(item: ItAssetItem): string | undefined {
  const parts = [
    item.parent_inventory ? 'it-admin-restricted__row--child' : '',
    ageBandClass(item),
  ].filter(Boolean)
  return parts.length ? parts.join(' ') : undefined
}

function toggleInSet<T extends string>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set)
  if (next.has(value)) next.delete(value)
  else next.add(value)
  return next
}

function cmpNullable(a: string | number | null | undefined, b: string | number | null | undefined, dir: SortDir): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') {
    return dir === 'asc' ? a - b : b - a
  }
  const as = String(a).toLocaleLowerCase('cs')
  const bs = String(b).toLocaleLowerCase('cs')
  const c = as.localeCompare(bs, 'cs', { numeric: true, sensitivity: 'base' })
  return dir === 'asc' ? c : -c
}

function sortValue(item: ItAssetItem, key: SortKey): string | number | null {
  const loc = parseItAssetLocation(item.location)
  switch (key) {
    case 'category':
      return categoryLabel(item.category)
    case 'inventory_number':
      return item.inventory_number
    case 'name':
      return item.name
    case 'building':
      return loc.building || null
    case 'room':
      return loc.room || null
    case 'user':
      return loc.user || null
    case 'monitor_location':
      return item.monitor_location ?? null
    case 'year_acquired':
      return item.year_acquired ?? null
    case 'accounting_price':
      return item.accounting_price ?? null
    case 'su_au':
      return item.su_au ?? null
    case 'note':
      return item.note ?? null
  }
}

export default function ITAdminRestrictedTable() {
  const navigate = useNavigate()
  const { user, loading } = useAuth()
  const [allowed, setAllowed] = useState<boolean | null>(null)

  const [search, setSearch] = useState('')
  const [buildingFilter, setBuildingFilter] = useState('')
  const [selectedBuildings, setSelectedBuildings] = useState<Set<string>>(() => new Set())
  const [roomFilter, setRoomFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [selectedComputerLabs, setSelectedComputerLabs] = useState<Set<string>>(() => new Set())
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => new Set())
  const [selectedAges, setSelectedAges] = useState<Set<AgeBand>>(() => new Set())
  const [yearFrom, setYearFrom] = useState('')
  const [yearTo, setYearTo] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('category')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const [allItems, setAllItems] = useState<ItAssetItem[]>([])
  const [counts, setCounts] = useState<{ total?: number; by_category?: Record<string, number> }>({})
  const [loadingData, setLoadingData] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const buildingOptions = useMemo(() => buildingOptionsFromItems(allItems), [allItems])
  const roomOptions = useMemo(
    () =>
      roomOptionsFromItems(allItems, {
        selectedBuildings,
        buildingQuery: buildingFilter,
      }),
    [allItems, selectedBuildings, buildingFilter],
  )
  const userOptions = useMemo(() => userOptionsFromItems(allItems), [allItems])

  const roomOptionsFiltered = useMemo(() => {
    const q = roomFilter.trim().toLowerCase()
    if (!q) return roomOptions
    return roomOptions.filter((room) => room.toLowerCase().includes(q))
  }, [roomOptions, roomFilter])

  const userOptionsFiltered = useMemo(() => {
    const q = userFilter.trim().toLowerCase()
    if (!q) return userOptions
    return userOptions.filter((user) => user.toLowerCase().includes(q))
  }, [userOptions, userFilter])

  const computerLabCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const lab of COMPUTER_LABS) counts.set(lab.id, 0)
    for (const item of allItems) {
      for (const lab of COMPUTER_LABS) {
        if (itemMatchesComputerLab(item, lab.id)) {
          counts.set(lab.id, (counts.get(lab.id) || 0) + 1)
        }
      }
    }
    return counts
  }, [allItems])

  const duplicateInventoryNumbers = useMemo(() => {
    const counts = new Map<string, number>()
    for (const item of allItems) {
      counts.set(item.inventory_number, (counts.get(item.inventory_number) || 0) + 1)
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k))
  }, [allItems])

  function formatInventoryNumber(item: ItAssetItem): string {
    if (duplicateInventoryNumbers.has(item.inventory_number)) {
      return `${item.inventory_number} · ${categoryLabel(item.category)}`
    }
    return item.inventory_number
  }

  useEffect(() => {
    if (loading || !user) return
    let cancelled = false
    getItAssetsAccess()
      .then((r) => {
        if (cancelled) return
        setAllowed(!!r.canAccess)
        if (!r.canAccess) navigate('/it-admin-info', { replace: true })
      })
      .catch(() => {
        if (!cancelled) {
          setAllowed(false)
          navigate('/it-admin-info', { replace: true })
        }
      })
    return () => {
      cancelled = true
    }
  }, [loading, user, navigate])

  useEffect(() => {
    if (allowed !== true) return
    let cancelled = false
    setLoadingData(true)
    setError(null)
    getItAssets()
      .then((res) => {
        if (cancelled) return
        setAllItems(Array.isArray(res.items) ? res.items : [])
        setCounts(res.counts || {})
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Nepodařilo se načíst evidenci')
          setAllItems([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingData(false)
      })
    return () => {
      cancelled = true
    }
  }, [allowed])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const buildingQ = buildingFilter.trim().toLowerCase()
    const roomQ = roomFilter.trim().toLowerCase()
    const userQ = userFilter.trim().toLowerCase()
    const yFrom = yearFrom.trim() ? Number(yearFrom) : null
    const yTo = yearTo.trim() ? Number(yearTo) : null
    const catOk = selectedCategories.size === 0
    const allowedCats = new Set<string>()
    if (!catOk) {
      for (const opt of CATEGORY_OPTIONS) {
        if (selectedCategories.has(opt.id)) {
          for (const c of opt.cats) allowedCats.add(c)
        }
      }
    }
    const ageOk = selectedAges.size === 0
    const buildingExactOk = selectedBuildings.size === 0
    const labOk = selectedComputerLabs.size === 0

    return allItems.filter((item) => {
      if (!catOk && !allowedCats.has(item.category)) return false
      if (!ageOk && !selectedAges.has(ageBandOf(item.year_acquired, item.category))) return false
      if (!labOk && !itemMatchesAnyComputerLab(item, selectedComputerLabs)) return false
      if (yFrom != null && !Number.isNaN(yFrom)) {
        if (item.year_acquired == null || item.year_acquired < yFrom) return false
      }
      if (yTo != null && !Number.isNaN(yTo)) {
        if (item.year_acquired == null || item.year_acquired > yTo) return false
      }

      const loc = parseItAssetLocation(item.location)
      if (!buildingExactOk) {
        if (!loc.building || !selectedBuildings.has(loc.building)) return false
      } else if (buildingQ) {
        if (!itemBuildingMatchesFilter(item.location, buildingQ)) return false
      }
      if (roomQ && !loc.room.toLowerCase().includes(roomQ)) return false
      if (userQ && !loc.user.toLowerCase().includes(userQ)) return false

      if (!q) return true
      const hay = [
        item.inventory_number,
        item.name,
        item.location,
        loc.building,
        loc.room,
        loc.user,
        item.note,
        item.su_au,
        item.monitor_location,
        item.parent_inventory,
        item.category,
        categoryLabel(item.category),
        item.source_file,
      ]
        .map((x) => String(x || '').toLowerCase())
        .join(' ')
      return hay.includes(q)
    })
  }, [
    allItems,
    search,
    buildingFilter,
    selectedBuildings,
    roomFilter,
    userFilter,
    selectedCategories,
    selectedAges,
    selectedComputerLabs,
    yearFrom,
    yearTo,
  ])

  const displayed = useMemo(() => {
    const rows = [...filtered]
    rows.sort((a, b) => {
      const primary = cmpNullable(sortValue(a, sortKey), sortValue(b, sortKey), sortDir)
      if (primary !== 0) return primary
      return cmpNullable(a.inventory_number, b.inventory_number, 'asc') || a.id - b.id
    })
    return rows
  }, [filtered, sortKey, sortDir])

  const hasActiveFilters =
    search.trim() !== '' ||
    buildingFilter.trim() !== '' ||
    selectedBuildings.size > 0 ||
    roomFilter.trim() !== '' ||
    userFilter.trim() !== '' ||
    selectedComputerLabs.size > 0 ||
    selectedCategories.size > 0 ||
    selectedAges.size > 0 ||
    yearFrom.trim() !== '' ||
    yearTo.trim() !== ''

  const resetFilters = () => {
    setSearch('')
    setBuildingFilter('')
    setSelectedBuildings(new Set())
    setRoomFilter('')
    setUserFilter('')
    setSelectedComputerLabs(new Set())
    setSelectedCategories(new Set())
    setSelectedAges(new Set())
    setYearFrom('')
    setYearTo('')
  }

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'year_acquired' || key === 'accounting_price' ? 'desc' : 'asc')
    }
  }

  const sortIndicator = (key: SortKey) => {
    if (sortKey !== key) return ' ↕'
    return sortDir === 'asc' ? ' ↑' : ' ↓'
  }

  if (loading || !user) {
    return (
      <div className="it-admin-restricted">
        <Header
          leftButtons={
            <>
              <button type="button" className="back-button" onClick={() => navigate('/it-admin-info', { replace: true })}>
                ← Zpět
              </button>
              <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Evidence IT majetku</h1>
            </>
          }
          showLogo={true}
        />
        <div className="it-admin-restricted__container">
          <p>Načítání…</p>
        </div>
      </div>
    )
  }

  if (loading || allowed === null) {
    return (
      <div className="it-admin-restricted">
        <Header title="Evidence IT majetku" showLogo />
        <div style={{ padding: '2rem', color: '#fff' }}>Načítání…</div>
      </div>
    )
  }

  if (!allowed) return null

  return (
    <div className="it-admin-restricted">
      <Header
        leftButtons={
          <>
            <button type="button" className="back-button" onClick={() => navigate('/it-admin-info', { replace: true })}>
              ← Zpět na informace IT
            </button>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Evidence IT majetku</h1>
          </>
        }
        showLogo={true}
      />
      <div className="it-admin-restricted__container">
        <div className="it-admin-restricted__toolbar">
          <label className="it-admin-restricted__field">
            <span>Hledat</span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Inventární číslo, název…"
            />
          </label>
          <label className="it-admin-restricted__field it-admin-restricted__field--narrow">
            <span>Budova</span>
            <input
              type="search"
              value={buildingFilter}
              onChange={(e) => setBuildingFilter(e.target.value)}
              placeholder="např. K-29, OB-6"
              autoComplete="off"
            />
          </label>
          <label className="it-admin-restricted__field">
            <span>Místnost</span>
            <input
              type="search"
              list="it-assets-rooms"
              value={roomFilter}
              onChange={(e) => setRoomFilter(e.target.value)}
              placeholder="učebna, kancelář…"
              autoComplete="off"
            />
            <datalist id="it-assets-rooms">
              {roomOptionsFiltered.slice(0, 80).map((room) => (
                <option key={room} value={room} />
              ))}
            </datalist>
          </label>
          <label className="it-admin-restricted__field">
            <span>Uživatel</span>
            <input
              type="search"
              list="it-assets-users"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder="příjmení…"
              autoComplete="off"
            />
            <datalist id="it-assets-users">
              {userOptionsFiltered.slice(0, 80).map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          </label>
          <label className="it-admin-restricted__field it-admin-restricted__field--narrow">
            <span>Rok od</span>
            <input
              type="number"
              inputMode="numeric"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              placeholder="např. 2018"
              min={1990}
              max={REF_YEAR}
            />
          </label>
          <label className="it-admin-restricted__field it-admin-restricted__field--narrow">
            <span>Rok do</span>
            <input
              type="number"
              inputMode="numeric"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              placeholder="např. 2024"
              min={1990}
              max={REF_YEAR}
            />
          </label>
          {hasActiveFilters ? (
            <button type="button" className="it-admin-restricted__reset" onClick={resetFilters}>
              Zrušit filtry
            </button>
          ) : null}
          <p className="it-admin-restricted__count">
            {loadingData ? 'Načítání…' : `Zobrazeno ${displayed.length}`}
            {counts.total != null ? ` z ${counts.total}` : ''}
          </p>
        </div>

        {buildingOptions.length > 0 ? (
          <div className="it-admin-restricted__filter-group it-admin-restricted__filter-group--inline">
            <span className="it-admin-restricted__filter-label">Budovy (více)</span>
            <div className="it-admin-restricted__chips">
              {buildingOptions.map((b) => {
                const on = selectedBuildings.has(b)
                return (
                  <button
                    key={b}
                    type="button"
                    className={`it-admin-restricted__chip${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setSelectedBuildings((s) => toggleInSet(s, b))}
                  >
                    {b}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}

        <div className="it-admin-restricted__filters">
          <div className="it-admin-restricted__filter-group">
            <span className="it-admin-restricted__filter-label">Počítačové učebny (více)</span>
            <div className="it-admin-restricted__chips">
              {COMPUTER_LABS.map((lab) => {
                const on = selectedComputerLabs.has(lab.id)
                const count = computerLabCounts.get(lab.id) || 0
                return (
                  <button
                    key={lab.id}
                    type="button"
                    className={`it-admin-restricted__chip it-admin-restricted__chip--lab${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    title={count > 0 ? `${count} položek v evidenci` : undefined}
                    onClick={() => setSelectedComputerLabs((s) => toggleInSet(s, lab.id))}
                  >
                    {lab.label} ({lab.teacher})
                  </button>
                )
              })}
            </div>
          </div>
          <div className="it-admin-restricted__filter-group">
            <span className="it-admin-restricted__filter-label">Typ (více možností)</span>
            <div className="it-admin-restricted__chips">
              {CATEGORY_OPTIONS.map((opt) => {
                const on = selectedCategories.has(opt.id)
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`it-admin-restricted__chip${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setSelectedCategories((s) => toggleInSet(s, opt.id))}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
          <div className="it-admin-restricted__filter-group">
            <span className="it-admin-restricted__filter-label">Stáří (více možností)</span>
            <div className="it-admin-restricted__chips">
              {AGE_OPTIONS.map((opt) => {
                const on = selectedAges.has(opt.id)
                return (
                  <button
                    key={opt.id}
                    type="button"
                    className={`it-admin-restricted__chip it-admin-restricted__chip--age-${opt.id}${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => setSelectedAges((s) => toggleInSet(s, opt.id))}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {error ? <p className="it-admin-restricted__empty">{error}</p> : null}

        <ul className="it-admin-restricted__legend" aria-label="Legenda stáří">
          <li>
            <span className="it-admin-restricted__swatch it-admin-restricted__swatch--new" />
            Nedávno pořízené
          </li>
          <li>
            <span className="it-admin-restricted__swatch it-admin-restricted__swatch--normal" />
            Normální životnost
          </li>
          <li>
            <span className="it-admin-restricted__swatch it-admin-restricted__swatch--attention" />
            Pozornost / profylaktika
          </li>
          <li>
            <span className="it-admin-restricted__swatch it-admin-restricted__swatch--replace" />
            Prioritní výměna
          </li>
        </ul>

        <div className="it-admin-restricted__table-wrap">
          <table className="it-admin-restricted__table">
            <thead>
              <tr>
                {(
                  [
                    ['category', 'Typ'],
                    ['inventory_number', 'Invent. číslo'],
                    ['name', 'Název'],
                    ['building', 'Budova'],
                    ['room', 'Místnost'],
                    ['user', 'Uživatel'],
                    ['monitor_location', 'Monitor'],
                    ['year_acquired', 'Rok'],
                    ['accounting_price', 'Účetní cena'],
                    ['su_au', 'Su/Au'],
                    ['note', 'Poznámka'],
                  ] as [SortKey, string][]
                ).map(([key, label]) => (
                  <th key={key}>
                    <button
                      type="button"
                      className={`it-admin-restricted__sort${sortKey === key ? ' is-active' : ''}`}
                      onClick={() => onSort(key)}
                    >
                      {label}
                      <span className="it-admin-restricted__sort-ind" aria-hidden>
                        {sortIndicator(key)}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loadingData && displayed.length === 0 ? (
                <tr>
                  <td colSpan={11} className="it-admin-restricted__empty">
                    {error
                      ? '—'
                      : counts.total === 0
                        ? 'Evidence je prázdná — spusťte import na serveru'
                        : 'Žádné položky neodpovídají filtru'}
                  </td>
                </tr>
              ) : (
                displayed.map((item) => {
                  const loc = parseItAssetLocation(item.location)
                  return (
                  <tr key={item.id} className={rowClassName(item)}>
                    <td>{categoryLabel(item.category)}</td>
                    <td className="it-admin-restricted__inv" title={item.source_file || undefined}>
                      {formatInventoryNumber(item)}
                    </td>
                    <td>{item.name}</td>
                    <td className="it-admin-restricted__loc-building">{loc.building || '—'}</td>
                    <td className="it-admin-restricted__loc-room">{loc.room || '—'}</td>
                    <td className="it-admin-restricted__loc-user">{loc.user || '—'}</td>
                    <td>{item.monitor_location || '—'}</td>
                    <td>{item.year_acquired ?? '—'}</td>
                    <td className="it-admin-restricted__price">{formatPrice(item.accounting_price)}</td>
                    <td>{item.su_au || '—'}</td>
                    <td>{item.note || '—'}</td>
                  </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
